import dayjs from 'dayjs'
import { DateTime } from 'luxon'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import {
  LuArrowBigDown,
  LuArrowBigUp,
  LuBan,
  LuExternalLink,
  LuInfo,
  LuSquareArrowOutUpRight,
  LuX,
} from 'react-icons/lu'
import { useTheme } from 'styled-components'

import { ExpectedAsset, ExpectedAssetView } from '@/feature/assets/components/ExpectedAssetView'
import { VehicleDetailsTooltip } from '@/feature/observatory'
import { useShareAssetModalStore } from '@/lib/stores/shareAssetModalStore'

import { Badge, BadgeVariant } from '@/lib/core/components/Badge'
import { Button, IconButton, TextButton } from '@/lib/core/components/Button'
import { Checkmark } from '@/lib/core/components/Checkmark'
import { CircleAvatar } from '@/lib/core/components/CircleAvatar'
import { FormattedMessage } from '@/lib/core/components/i18n/formatjs-disable'
import { Pagination, PaginationPayload } from '@/lib/core/components/Pagination'
import { RadioButtons } from '@/lib/core/components/RadioButtons'
import { Table, TableColumns, Td, Tr } from '@/lib/core/components/Table'
import {
  Actions,
  ExpiresSoon,
  ExpiresSoonContainer,
  Owner,
  Row,
} from '@/lib/core/components/Table/Table.styles'
import { Tooltip } from '@/lib/core/components/Tooltip'
import { Truncate } from '@/lib/core/components/Truncate'
import { TypeBadge } from '@/lib/core/components/TypeBadge'
import { useSelectedRows } from '@/lib/core/hooks/useSelectedRows'
import { SmartShareListFilters, SortDirection } from '@/lib/core/interfaces/common.types'

import assetViewMessages from '@/lib/features/Assets/components/AssetView/assetView.messages'
import { MultipleSelectBar } from '@/lib/features/Assets/components/MultipleSelectBar'
import selectBarMessages from '@/lib/features/Assets/components/MultipleSelectBar/MultipleSelectBar.messages'
import { Asset, AssetContentType, BridgeStatus } from '@/lib/features/Assets/interfaces'
import messages from '@/lib/features/SmartShares/components/ActiveTable/ActiveTable.messages'
import { CancelSmartShareModal } from '@/lib/features/SmartShares/components/CancelSmartShareModal'
import { EditableReference } from '@/lib/features/SmartShares/components/EditableReference'
import { getOptionLabel } from '@/lib/features/SmartShares/components/ShareAssetModal/components'
import {
  EXTEND_BLOCKED_TOOLTIP,
  isDelegatedShare,
  ONWARD_SHARE_BLOCKED_TOOLTIP,
} from '@/lib/features/SmartShares/components/ShareAssetModal/utils'
import { SwitchStatus } from '@/lib/features/SmartShares/components/SwitchStatus'
import { ShareId } from '@/lib/features/SmartShares/interfaces'

import { checkIsDateAndConvertToBackendStandard, formatDate } from '@/lib/utils/date-time'

import { useActiveShares } from '../../api'
import { ActiveTableProps } from './ActiveTable.types'

const TABLE_COLUMNS: TableColumns = [
  {
    accessor: 'seen_at',
    sortable: false,
    label: '',
  },
  {
    accessor: 'bridge_id',
    sortKey: 'bridge_id',
    label: messages.assetId.defaultMessage,
    sortable: true,
    width: '100px',
  },
  {
    accessor: 'description',
    sortKey: 'asset__description',
    sortable: true,
    label: messages.description.defaultMessage,
  },
  {
    accessor: 'License Plate',
    sortKey: 'plate_number',
    sortable: true,
    label: messages.plateNumber.defaultMessage,
  },
  {
    accessor: 'reference',
    sortKey: 'reference',
    sortable: true,
    label: messages.reference.defaultMessage,
    width: '200px',
  },
  {
    accessor: 'owner',
    sortKey: 'organization_name',
    sortable: true,
    label: messages.owner.defaultMessage,
  },
  {
    accessor: 'type',
    sortKey: 'bridge_status',
    sortable: true,
    label: messages.type.defaultMessage,
  },
  {
    accessor: 'issued_on',
    sortKey: 'issued_on',
    sortable: true,
    label: messages.issuedOn.defaultMessage,
  },
  {
    accessor: 'bridge_date_from',
    sortKey: 'date_from',
    sortable: true,
    label: messages.bridgeDateFrom.defaultMessage,
  },
  {
    accessor: 'bridge_date_to',
    sortKey: 'date_to',
    sortable: true,
    label: messages.bridgeDateTo.defaultMessage,
    width: '225px',
  },
  {
    accessor: 'is_active',
    sortKey: 'is_active',
    sortable: true,
    label: messages.tracking.defaultMessage,
    textAlign: 'center',
    width: '97px',
  },
  {
    accessor: 'actions',
    label: 'Actions',
    textAlign: 'start',
    width: '228px',
  },
]

export const ActiveTable = ({
  defaultFilters = {},
  onUpdate = () => null,
  onDelete = async () => false,
  onFiltersChange = () => null,
  onShare = () => null,
  dataTestId,
}: ActiveTableProps) => {
  const { colors } = useTheme()
  const [filters, setFilters] = useState<SmartShareListFilters>(defaultFilters)
  const { data, isLoading, mutate } = useActiveShares(filters, {
    fallbackData: {
      count: 0,
      countAll: 0,
      countLent: 0,
      countBorrowed: 0,
      countUnseen: 0,
      countOnward: 0,
      next: null,
      previous: null,
      results: [],
    },
  })

  const { openModal: openShareAssetModal } = useShareAssetModalStore()

  const router = useRouter()
  const { onRowSelect, resetSelectedRows, onCheckAll, selectedRows } = useSelectedRows(
    data.results,
    {},
    'id',
  )

  const [allRowsChecked, setAllRowsChecked] = useState(false)
  const [openCancelModal, setOpenCancelModal] = useState(false)

  const searchParams = useSearchParams()

  const [expectedAsset, setExpectedAsset] = useState<ExpectedAsset | null>(null)

  const handleDeleteAsset = async (type: AssetContentType, id: Asset['objectId']) => {
    const isDeleted = await onDelete(type, id)

    if (isDeleted) {
      setExpectedAsset(null)
    }

    return isDeleted
  }

  const handleShareAsset = (asset: Asset) => {
    onShare([
      {
        label: getOptionLabel(asset),
        value: asset.id as number,
      },
    ])
  }

  const closeModal = (confirmed?: boolean) => {
    onDeselected()

    if (confirmed) {
      onUpdate()
    }
  }

  const onPaginationChange = (paginationFilters: PaginationPayload) => {
    setFilters({
      ...filters,
      ...paginationFilters,
    })
  }

  const onSearch = (search?: string) => {
    if (search) {
      const convertedDate = checkIsDateAndConvertToBackendStandard(search)
      if (convertedDate) {
        setFilters({
          ...filters,
          page: 1,
          search: convertedDate,
        })
        return
      }
    }

    setFilters({
      ...filters,
      page: 1,
      search,
    })
  }

  const onFilterType = (shareType: string) => {
    setURLParams(shareType)

    setFilters({
      ...filters,
      page: 1,
      shareType: shareType,
    })
  }

  const clearSearch = () => {
    setURLParams('all')
    setFilters({
      ...filters,
      page: 1,
      search: undefined,
      shareType: undefined,
    })
  }

  const onSortChange = (sortKey: string, direction: SortDirection | undefined) => {
    setFilters({
      ...filters,
      ordering: direction ? { [sortKey]: direction } : {},
    })
  }

  const setURLParams = (value: string) => {
    router.push({ query: { filter: value, table: 'active' } }, undefined, { shallow: true })
  }

  useEffect(() => onFiltersChange(filters), [filters])

  useEffect(() => {
    const filterParam = searchParams?.get('filter')
    if (filterParam) {
      const assetBridgeId = searchParams?.get('assetBridgeId')
      setFilters({
        ...filters,
        page: 1,
        search: assetBridgeId || undefined,
        shareType: filterParam,
      })
    }
  }, [searchParams])

  const onDeselected = () => {
    setAllRowsChecked(false)
    resetSelectedRows()
    setOpenCancelModal(false)
  }

  const onCancelMultipleSmartShares = () => {
    setOpenCancelModal(true)
  }

  const onSelectSingleCancel = (id: ShareId) => {
    onRowSelect(id)
    setOpenCancelModal(true)
  }

  const selectedShareIds = useMemo(() => {
    return Object.keys(selectedRows).map((sr) => parseInt(sr))
  }, [selectedRows])

  const filterParams = useSearchParams()?.get('filter')

  return expectedAsset ? (
    <ExpectedAssetView
      expectedAsset={expectedAsset}
      onClose={() => {
        onUpdate()
        setExpectedAsset(null)
      }}
      onDelete={handleDeleteAsset}
      onShare={handleShareAsset}
    />
  ) : (
    <div data-testid={dataTestId}>
      <CancelSmartShareModal
        closeModal={closeModal}
        shareIds={selectedShareIds}
        isOpen={openCancelModal}
      />

      <Table
        columns={TABLE_COLUMNS}
        isLoading={isLoading}
        elementsCounter={data.count}
        isSelectable
        checkAll={(allChecked) => {
          setAllRowsChecked(allChecked)
          onCheckAll(allChecked)
        }}
        externalAllChecked={allRowsChecked}
        onSearch={onSearch}
        clearSearch={clearSearch}
        onSort={onSortChange}
        sortInfo={filters.ordering}
        renderCustomFilter={
          <RadioButtons
            additionalStyles={{
              minHeight: 40,
            }}
            currentValue={defaultFilters.shareType ?? filterParams ?? 'all'}
            name={`smart-share-type-filter`}
            onChange={(value) => {
              if (value === 'all') {
                clearSearch()
              } else {
                onFilterType(value)
              }
            }}
            options={[
              {
                label: `All (${data.countAll ?? 0})`,
                value: 'all',
              },
              {
                label: `Inbound (${data.countBorrowed ?? 0})`,
                value: 'borrowed',
              },
              {
                label: `Outbound (${data.countLent ?? 0})`,
                value: 'lent',
              },
              {
                label: `Onward (${data.countOnward ?? 0})`,
                value: 'onward',
              },
            ]}
          />
        }
      >
        {data.results.map(
          (
            {
              assetId,
              bridgeId,
              bridgeStatus,
              canToggleMap,
              contactTo,
              contactFrom,
              contentType,
              dateFrom,
              dateTo,
              description,
              id,
              issuedOn,
              make,
              model,
              organization,
              reference,
              seenAtByOrganizationTo,
              plateNumber,
              isActive,
              shareClass,
            },
            i,
          ) => (
            <Tr key={`${bridgeId}-${i.toString()}`}>
              <Td>
                <Checkmark isActive={selectedRows[id]} onClick={() => onRowSelect(id)} />
              </Td>
              <Td>
                {bridgeStatus === BridgeStatus.BORROWED && !seenAtByOrganizationTo && (
                  <Badge variant={BadgeVariant.ACCENT} />
                )}
              </Td>
              <Td>
                <Row>
                  {bridgeId}
                  <Tooltip
                    interactive
                    trigger="click"
                    arrowOffset={50}
                    contentPadding={0}
                    placement="left-end"
                    tooltipContent={
                      <VehicleDetailsTooltip
                        make={make}
                        model={model}
                        plateNumber={plateNumber}
                        id={assetId}
                        organization={organization}
                      />
                    }
                  >
                    <IconButton type="button" $variant="primary">
                      <LuInfo size="16" color={colors.active} strokeWidth={1.6} />
                    </IconButton>
                  </Tooltip>
                </Row>
              </Td>
              <Td>
                <Truncate content={description} limit={10} />
              </Td>
              <Td>
                <Truncate content={plateNumber} limit={10} />
              </Td>
              <Td>
                <EditableReference
                  share={{
                    id,
                    assetId,
                    bridgeStatus,
                    contactFrom,
                    contactTo,
                    dateFrom,
                    dateTo,
                    organization,
                    reference,
                  }}
                  onUpdated={() => mutate()}
                />
              </Td>
              <Td>
                <Owner>
                  <CircleAvatar src={organization.avatar} placeholder="empty" size={27.5} />
                  {organization.name}
                </Owner>
              </Td>
              <Td>
                {bridgeStatus === BridgeStatus.BORROWED && (
                  <TypeBadge $variant="success">
                    <LuArrowBigDown size="16" />
                    {messages.in.defaultMessage}
                  </TypeBadge>
                )}
                {bridgeStatus === BridgeStatus.LENT && (
                  <TypeBadge $variant="warning">
                    <LuArrowBigUp size="16" />
                    {messages.out.defaultMessage}
                  </TypeBadge>
                )}
              </Td>
              <Td>{formatDate(issuedOn)}</Td>
              <Td>{formatDate(dateFrom)}</Td>
              <Td>
                <ExpiresSoonContainer>
                  {formatDate(dateTo)}{' '}
                  {DateTime.fromISO(dateTo).hasSame(DateTime.local(), 'day') ? (
                    <ExpiresSoon>EXPIRES TODAY</ExpiresSoon>
                  ) : (
                    ''
                  )}
                </ExpiresSoonContainer>
              </Td>
              <Td $align="center">
                {canToggleMap &&
                  bridgeStatus === BridgeStatus.LENT &&
                  dayjs().isBetween(dateFrom, dateTo, 'day', '[]') && (
                    <div
                      style={{ alignItems: 'center', display: 'flex', justifyContent: 'center' }}
                    >
                      <SwitchStatus shareId={id} defaultValue={isActive} />
                    </div>
                  )}
              </Td>
              <Td>
                <Actions>
                  <IconButton
                    type="button"
                    $variant="primary"
                    // This one action extends a share you lent and passes on one you borrowed.
                    // A delegation is a fixed grant, so neither applies to it — the backend
                    // rejects both, and stopping here saves filling the form in first.
                    disabled={isDelegatedShare({ shareClass })}
                    title={
                      isDelegatedShare({ shareClass })
                        ? bridgeStatus === BridgeStatus.BORROWED
                          ? ONWARD_SHARE_BLOCKED_TOOLTIP
                          : EXTEND_BLOCKED_TOOLTIP
                        : undefined
                    }
                    data-testid={`share-onward-${id}-button`}
                    onClick={() => {
                      const shareData = {
                        assetId,
                        bridgeId,
                        bridgeStatus,
                        canToggleMap,
                        contentType,
                        dateFrom,
                        dateTo,
                        contactTo,
                        contactFrom,
                        description,
                        id,
                        issuedOn,
                        make,
                        model,
                        organization,
                        reference,
                        seenAtByOrganizationTo,
                        plateNumber,
                        isActive,
                        shareClass,
                      }

                      openShareAssetModal({
                        shareToExtend: bridgeStatus === BridgeStatus.LENT ? shareData : undefined,
                        onwardShareToExtend:
                          bridgeStatus === BridgeStatus.BORROWED ? shareData : undefined,
                        onSuccess: () => {
                          mutate()
                        },
                      })
                    }}
                  >
                    {bridgeStatus === BridgeStatus.BORROWED ? (
                      <LuSquareArrowOutUpRight size="24" strokeWidth={1.5} />
                    ) : (
                      <LuExternalLink size="24" strokeWidth={1.5} />
                    )}
                  </IconButton>

                  <Button
                    type="button"
                    variant="secondary"
                    size="medium"
                    style={{
                      visibility:
                        bridgeStatus === BridgeStatus.LENT || !dayjs().isBefore(dateFrom, 'day')
                          ? 'visible'
                          : 'hidden',
                    }}
                    onClick={() =>
                      setExpectedAsset({
                        assetId,
                        bridgeId,
                        contentType,
                        description,
                      })
                    }
                    $fitToContent
                  >
                    {assetViewMessages.view.defaultMessage}
                  </Button>
                  <TextButton type="button" onClick={() => onSelectSingleCancel(id)}>
                    {assetViewMessages.cancel.defaultMessage}
                  </TextButton>
                </Actions>
              </Td>
            </Tr>
          ),
        )}
      </Table>
      <Pagination
        initialPage={filters.page}
        initialPageSize={filters.pageSize}
        isLoading={isLoading}
        onChange={onPaginationChange}
        recordsCount={data.count}
      />

      {selectedShareIds.length > 0 && (
        <MultipleSelectBar
          actions={
            <>
              <Button
                variant="ghost"
                leftIcon={<LuBan size={24} />}
                size="large"
                style={{ fontSize: 12 }}
                onClick={onCancelMultipleSmartShares}
                lightVariant
              >
                {selectBarMessages.cancelSmartShares.defaultMessage}
              </Button>
              <Button
                leftIcon={<LuX size={24} strokeWidth={1.5} />}
                variant="text"
                size="large"
                style={{ width: 100 }}
                onClick={onDeselected}
                lightVariant
              />
            </>
          }
          message={
            <FormattedMessage
              {...selectBarMessages.selectedSmartSharesCounter}
              values={{
                count: selectedShareIds.length,
              }}
            />
          }
        />
      )}
    </div>
  )
}
